import type { JWTHeader } from "../types/JWT"
import type { Time } from "../types/Units"
import { StringToUInt8 } from "../bytes/StringToUInt8"
import { Base64URLEncode } from "../encoding/Base64URLEncode"
import { GUIDToUint8 } from "../encoding/GUIDToUint8"
import { JSONEncode } from "../encoding/JSONEncode"
import { HMACSha256 } from "../hashing/HMACSha256"
import { UnixTime } from "../time/UnixTime"

/**
 * Creates signed JWT token using HMAC-SHA256.
 *
 * Encodes payload as JWT with iat and exp claims.
 * Compatible with standard JWT libraries.
 *
 * @param payload - Data to encode in JWT
 * @param secret - Secret key for signing
 * @param seconds - Token validity duration in seconds (default: 3600)
 * @param current - Override current time for testing (optional)
 * @returns Promise resolving to signed JWT string
 *
 * @example
 * ```ts
 * import { EncodeJWT } from "@pdfking/tfunc"
 *
 * const token = await EncodeJWT({ userId: 123 }, "secret", 3600)
 * // "eyJhbGc...header.payload.signature"
 * ```
 */
export const EncodeJWT = async <T>(
  payload: T,
  secret: string,
  seconds: number = 3600,
  current?: Time
): Promise<string> => {
  // Create header
  const header: JWTHeader = {
    alg: "HS256",
    typ: "JWT",
  }

  // Add JWT standard claims
  const now = current || UnixTime() // Current time in seconds
  const payloadWithClaims = {
    ...payload,
    exp: now + seconds, // Expires in specified seconds (default: 1 hour)
    iat: now, // Issued at
  }

  // Encode header and payload
  const encodedHeader = Base64URLEncode(StringToUInt8(JSONEncode(header)))
  const encodedPayload = Base64URLEncode(
    StringToUInt8(JSONEncode(payloadWithClaims))
  )

  // Create the message to sign (header.payload)
  const message = `${encodedHeader}.${encodedPayload}`
  const messageBytes = StringToUInt8(message)
  const secretBytes = StringToUInt8(secret)

  // Create signature using HMAC-SHA256
  const signatureGuid = await HMACSha256(secretBytes, messageBytes)
  const signatureBytes = GUIDToUint8(signatureGuid)
  const encodedSignature = Base64URLEncode(signatureBytes)

  // Return the complete JWT
  return `${message}.${encodedSignature}`
}
